import { useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";

/**
 * useSmartBack — safer alternative to `navigate(-1)`.
 *
 * Problem: `navigate(-1)` calls `history.back()` blindly. When a user lands
 * on a deep-linked page (clicked an email link, pasted a URL, came from a
 * Stripe redirect, opened from a push notification, or installed the PWA
 * and the cold-start opens to a sub-route) there is no previous entry in
 * the session history → the browser either does nothing, navigates outside
 * the SPA, or shows a blank screen. Users reported this as "back button
 * is broken".
 *
 * Solution: try `navigate(-1)` only if there's actual history to go back
 * to (`history.state?.idx > 0` is React Router's internal counter on its
 * memory router; `window.history.length > 1` is the browser fallback). If
 * neither indicates a real entry, fall back to a sensible parent route
 * that the caller declares via `fallback` (e.g. "/profile", "/trips",
 * "/admin"). Final fallback is the home page.
 *
 * Usage:
 *   const goBack = useSmartBack("/profile");
 *   <Button onClick={goBack}>Back</Button>
 *
 * @param {string} fallback - Route to navigate to when there's no history.
 * @returns {() => void}
 */
export default function useSmartBack(fallback = "/") {
  const navigate = useNavigate();
  const location = useLocation();
  return useCallback(() => {
    // React Router 6 stores its history index on the location state's
    // `idx` (private API but stable across minor versions). When `idx > 0`
    // there's a previous in-app entry.
    const idx = (window?.history?.state && window.history.state.idx) ?? 0;
    if (idx > 0) {
      navigate(-1);
      return;
    }
    // Final fallback: don't strand the user. The `replace: true` matters
    // — without it, "back" from the fallback would land on the same
    // deep-linked page again, creating a loop.
    navigate(fallback, { replace: true });
    // location is intentionally part of the dep list so callers in modal
    // routes get a fresh callback if the location changes.
  }, [navigate, fallback, location.key]);
}
